import { GetProjectFiltersQuery, TagDetailFragment } from '@/gql/generated/graphql'
import { FilterOption, Option } from '@components/grid/filter-panel'
import { useState } from 'react'
import { ValidUserTokensSort } from '../user-token-grid/utils'
import { ALL_VERTICALS } from '@utils/project'
import { useSearchTerms, SearchTermsState } from '@components/omnisearch'

export type useShowcaseEditorFiltersReturn = {
  sortOrder: ValidUserTokensSort
  sortOptions: ValidUserTokensSort[]
  setSortOrder: (newSortOrder: ValidUserTokensSort) => void
  tagsFilter: FilterOption
  categoryFilter: FilterOption
  clearFilters: () => void
  numberOfActiveFilters: number
  tagsFilterValue: string[]
  categoryFilterValue: string[]
} & SearchTermsState

export function useShowcaseEditorFilters({
  projectFiltersData
}: {
  projectFiltersData?: GetProjectFiltersQuery
}): useShowcaseEditorFiltersReturn {
  // Use local state instead of URL state for editor mode
  const [sortOrder, setSortOrder] = useState<ValidUserTokensSort>(ValidUserTokensSort.newest_collection)
  const [tagsFilterValue, setTagsFilterValue] = useState<string[]>([])
  const [categoryFilterValue, setCategoryFilterValue] = useState<string[]>([])
  const [localSearchTerms, setLocalSearchTerms] = useState<string[]>([])

  // Use shared search terms utility with local state
  const searchState = useSearchTerms({
    localState: {
      searchTerms: localSearchTerms,
      setSearchTerms: setLocalSearchTerms
    }
  })

  const buildTagOptions = (tags: TagDetailFragment[]): Option[] => {
    return tags.map((tag) => ({
      label: tag.display_name,
      value: tag.name
    }))
  }

  const setTagsFilterValueHandler = (value: string, selected: boolean) => {
    const newTagsFilterValue = selected ? [...tagsFilterValue, value] : tagsFilterValue.filter((v) => v !== value)
    setTagsFilterValue(newTagsFilterValue)
  }

  const tagsFilterOptions = [...buildTagOptions(projectFiltersData?.presentationTags ?? [])]

  const setCategoryFilterValueHandler = (value: string, selected: boolean) => {
    const newCategoryFilterValue = selected
      ? [...categoryFilterValue, value]
      : categoryFilterValue.filter((v) => v !== value)
    setCategoryFilterValue(newCategoryFilterValue)
  }

  const verticalFilterOptions = [
    ...(ALL_VERTICALS.map((vertical) => ({
      label: vertical.charAt(0).toUpperCase() + vertical.slice(1),
      value: vertical
    })) ?? [])
  ]

  const categoryFilter = {
    name: 'Collection category',
    selectedOptions: categoryFilterValue,
    options: verticalFilterOptions,
    onChange: setCategoryFilterValueHandler
  }

  const clearFilters = () => {
    setTagsFilterValue([])
    setCategoryFilterValue([])
    searchState.clearSearchTerms()
  }

  const numberOfActiveFilters = tagsFilterValue.length + categoryFilterValue.length + localSearchTerms.length

  return {
    sortOrder,
    sortOptions: Object.values(ValidUserTokensSort),
    setSortOrder,
    tagsFilter: {
      name: 'Collection tag',
      selectedOptions: tagsFilterValue,
      options: tagsFilterOptions,
      onChange: setTagsFilterValueHandler
    },
    categoryFilter,
    clearFilters,
    numberOfActiveFilters,
    tagsFilterValue,
    categoryFilterValue,
    ...searchState
  }
}
